(() => {
  const root = document.querySelector('[data-ale-journal-article]');
  if (!root) return;

  const body = root.querySelector('[data-journal-article-body]');
  const toc = root.querySelector('[data-journal-toc]');
  const list = root.querySelector('[data-journal-toc-list]');
  if (!body) return;

  const normalizeText = (text) =>
    (text || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[:.!]+$/, '')
      .toLowerCase();

  const unwrap = (element) => {
    if (!element?.parentNode) return;
    while (element.firstChild) element.parentNode.insertBefore(element.firstChild, element);
    element.remove();
  };

  const cleanupLegacyBody = () => {
    const source = body.innerHTML;
    const isBloggle = /bggle|bloggle/i.test(source);
    const isQuill = /ql-block|ql-heading/i.test(source);

    /* Third-party runtime assets belong to the retired editors, not The Letter. */
    body.querySelectorAll('script, style, link[rel="stylesheet"]').forEach((el) => el.remove());

    if (isBloggle) {
      body.querySelectorAll('#bloggy--article, .bggle_reco_blog_posts, .bggle_spacer').forEach((el) => el.remove());
      body.querySelectorAll('[class*="bggle"], [class*="bloggle"]').forEach((el) => {
        if (['DIV', 'SECTION'].includes(el.tagName)) unwrap(el);
        else el.removeAttribute('class');
      });

      /* Bloggle frequently leaves one or two anonymous wrapper divs after its classes are removed. */
      [...body.querySelectorAll('div')].reverse().forEach((el) => {
        const className = el.getAttribute('class') || '';
        if (!className.includes('ale-') && !el.hasAttribute('data-ale-preserve')) unwrap(el);
      });
    }

    if (isQuill) {
      body.querySelectorAll('.ql-block').forEach((el) => {
        const p = document.createElement('p');
        p.innerHTML = el.innerHTML;
        el.replaceWith(p);
      });
      body.querySelectorAll('.ql-heading').forEach((el) => el.removeAttribute('class'));
    }

    body.querySelectorAll('[style]').forEach((el) => {
      if (!el.closest('[class*="ale-media-"]')) el.removeAttribute('style');
    });
    body.querySelectorAll('span').forEach((el) => {
      if (![...el.attributes].length) unwrap(el);
      else if (el.attributes.length === 1 && el.hasAttribute('style')) unwrap(el);
    });
    body.querySelectorAll('br').forEach((el) => el.remove());

    body.querySelectorAll('p, h2, h3, h4').forEach((el) => {
      if (!normalizeText(el.textContent) && !el.querySelector('img, video, iframe')) el.remove();
    });

    /* Lists imported from Bloggle often wrap each item in a redundant paragraph. */
    body.querySelectorAll('li > p:only-child').forEach((p) => unwrap(p));
  };

  const enhanceNativePullQuotes = () => {
    for (const quote of [...body.querySelectorAll(':scope > blockquote')]) {
      if (quote.classList.contains('ale-letter-pull-quote')) continue;
      quote.classList.add('ale-letter-pull-quote', 'ale-letter-pull-quote--native');
    }
  };

  /* Permanent authoring convention:
     a standalone label + the immediately following paragraph = one callout.
     The theme does not guess based on paragraph length or wording. */
  const wrapLabelAndNextParagraph = (label, className) => {
    const parent = label.parentElement;
    if (!parent || parent !== body) return;

    const copy = label.nextElementSibling;
    if (!copy || !copy.matches('p')) return;

    const section = document.createElement('section');
    section.className = className;
    parent.insertBefore(section, label);
    section.append(label, copy);
  };

  const enhanceCallouts = () => {
    for (const el of [...body.querySelectorAll(':scope > p, :scope > h3, :scope > h4')]) {
      const label = normalizeText(el.textContent);
      if (label === 'put simply') {
        el.classList.add('ale-letter-callout__label', 'ale-journal-put-simply');
        wrapLabelAndNextParagraph(el, 'ale-letter-callout ale-letter-callout--put-simply');
      }
      if (label === 'a lovely reminder') {
        el.classList.add('ale-letter-callout__label');
        wrapLabelAndNextParagraph(el, 'ale-letter-callout ale-letter-callout--reminder');
      }
    }
  };

  const buildHighNotes = () => {
    if (body.querySelector('.ale-letter-high-notes, [data-ale-high-notes]')) return;
    const firstHeading = body.querySelector(':scope > h2');
    if (!firstHeading) return;

    const candidates = [];
    for (const node of [...body.childNodes]) {
      if (node === firstHeading) break;
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) continue;
      if (node.nodeType === Node.ELEMENT_NODE && node.matches('script, style, link')) continue;
      candidates.push(node);
    }
    if (!candidates.length) return;

    const section = document.createElement('section');
    section.className = 'ale-letter-high-notes';
    section.setAttribute('data-ale-high-notes', '');

    const label = document.createElement('p');
    label.className = 'ale-letter-high-notes__label';
    label.textContent = 'THE HIGH NOTES';
    section.appendChild(label);

    candidates.forEach((node) => section.appendChild(node));
    body.insertBefore(section, body.firstChild);
  };

  cleanupLegacyBody();
  enhanceNativePullQuotes();
  enhanceCallouts();
  buildHighNotes();

  if (toc && list) {
    const headings = [...body.querySelectorAll(':scope > h2')];
    if (headings.length) {
      const used = new Set();
      const slugify = (text) => {
        const base = (text || 'section')
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'section';
        let slug = base;
        let i = 2;
        while (used.has(slug) || document.getElementById(slug)) slug = `${base}-${i++}`;
        used.add(slug);
        return slug;
      };

      headings.forEach((heading) => {
        if (!heading.id) heading.id = slugify(heading.textContent);
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${heading.id}`;
        a.textContent = heading.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
      });
      toc.hidden = false;
    }
  }

  const tocDetails = root.querySelector('.ale-letter-toc');
  if (tocDetails) {
    tocDetails.addEventListener('toggle', () => {
      const toggle = tocDetails.querySelector('.ale-letter-toc__toggle');
      if (toggle) toggle.textContent = tocDetails.open ? '−' : '+';
    });
  }
})();
